import React, { useEffect, useMemo, useRef, useState } from "react";
import "./styles.scss";
import { Link, useNavigate } from "react-router-dom";
import {
  usePerformRequest,
  PerformRequest,
} from "../../../lib/usePerformRequest";
import { Endpoints } from "../../../lib/endpoints";
import { useOrganizationsContext } from "../../../context/OrganizationsContext";
import { Select } from "@mantine/core";
import { Loader } from "../../../components/ui/loader";
import { EmptyState } from "../../../components/ui/empty-state";
import { IEventProps, NonPaginatedResponse } from "../../../types";

type EventItem = {
  id: string;
  name: string;
  schedule: string;
  status: "Draft" | "Published" | "Live";
  registrations: {
    current: number;
    capacity: number;
  };
  cover: string;
  daysToGo: number;
  location: string;
  type: string;
  watchers: number;
  conversion: number;
  revenue: string;
  highlights: string[];
};

// Helper function to calculate days until event
const calculateDaysToGo = (dateString: string): number => {
  const eventDate = new Date(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  eventDate.setHours(0, 0, 0, 0);
  const diffTime = eventDate.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays > 0 ? diffDays : 0;
};

// Helper function to format date for display
const formatEventSchedule = (dateString: string): string => {
  const date = new Date(dateString);
  const options: Intl.DateTimeFormatOptions = {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  };
  return date.toLocaleDateString("en-US", options);
};

const statusFilters = [
  { label: "All upcoming", value: "all" },
  { label: "Draft only", value: "draft" },
  { label: "Published", value: "published" },
  { label: "Live now", value: "live" },
] as const;

const EventsPage: React.FC = () => {
  const [statusFilter, setStatusFilter] =
    useState<(typeof statusFilters)[number]["value"]>("all");
  const [filterOpen, setFilterOpen] = useState(false);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const filterRef = useRef<HTMLDivElement | null>(null);

  // Event type filter: "user" or "organization"
  const [eventTypeFilter, setEventTypeFilter] = useState<
    "user" | "organization"
  >("user");
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<
    number | null
  >(null);

  // Get organizations from context
  const { organizations, isLoading: isLoadingOrgs } = useOrganizationsContext();

  // Fetch user events
  const userEventsEndpoint = Endpoints.GetUserEvents();
  const { data: userEventsResponse, isLoading: isLoadingUserEvents } =
    usePerformRequest<{ events: IEventProps[] }>({
      method: userEventsEndpoint.method as any,
      route: userEventsEndpoint.route,
    });
  console.log("userEventsResponse", userEventsResponse);

  // State for organization events
  const [organizationEvents, setOrganizationEvents] = useState<any[]>([]);
  const [isLoadingOrgEvents, setIsLoadingOrgEvents] = useState(false);

  // Fetch organization events when organization is selected
  useEffect(() => {
    const fetchOrganizationEvents = async () => {
      if (eventTypeFilter !== "organization" || !selectedOrganizationId) {
        setOrganizationEvents([]);
        return;
      }

      setIsLoadingOrgEvents(true);
      try {
        const endpoint = Endpoints.GetOrganizationEvents(
          selectedOrganizationId
        );
        const response = await PerformRequest<{ events: IEventProps[] }>({
          method: endpoint.method as any,
          route: endpoint.route,
        });

        if (response.status === 200) {
          let events = [];
          if (Array.isArray(response.data)) {
            events = response.data;
          } else if (
            response.data?.events &&
            Array.isArray(response.data.events)
          ) {
            events = response.data.events;
          } else if (
            typeof response.data === "object" &&
            response.data !== null
          ) {
            // Try to extract events from various possible response structures
            const data = response.data as any;
            if (Array.isArray(data.events)) {
              events = data.events;
            } else if (Array.isArray(data.data?.events)) {
              events = data.data.events;
            } else if (Array.isArray(data.results)) {
              events = data.results;
            }
          }
          setOrganizationEvents(events);
        } else {
          setOrganizationEvents([]);
        }
      } catch (error) {
        console.error("Error fetching organization events:", error);
        setOrganizationEvents([]);
      } finally {
        setIsLoadingOrgEvents(false);
      }
    };

    fetchOrganizationEvents();
  }, [eventTypeFilter, selectedOrganizationId]);

  // Organization options for dropdown
  const organizationOptions = useMemo(() => {
    return organizations.map((org) => ({
      value: org.id.toString(),
      label: org.name,
    }));
  }, [organizations]);

  // Auto-select first organization when switching to organization filter
  useEffect(() => {
    if (
      eventTypeFilter === "organization" &&
      organizations.length > 0 &&
      !selectedOrganizationId
    ) {
      setSelectedOrganizationId(organizations[0].id);
    }
  }, [eventTypeFilter, organizations, selectedOrganizationId]);

  // Extract user events from response
  const userEventsRaw = useMemo(() => {
    if (!userEventsResponse) return [];
    if (Array.isArray(userEventsResponse)) return userEventsResponse;
    if (userEventsResponse.events && Array.isArray(userEventsResponse.events))
      return userEventsResponse.events;
    return [];
  }, [userEventsResponse]);

  // Map API event data to EventItem format
  const mapEventToEventItem = (event: any): EventItem => {
    const eventDate =
      event.date || event.created_at || new Date().toISOString();
    const daysToGo = calculateDaysToGo(eventDate);

    // Determine status (you may need to adjust based on your API)
    let status: "Draft" | "Published" | "Live" = "Draft";
    if (event.status) {
      const statusLower = event.status.toLowerCase();
      if (statusLower === "published" || statusLower === "live") {
        status = (statusLower.charAt(0).toUpperCase() +
          statusLower.slice(1)) as "Published" | "Live";
      }
    }

    // Get location from location object or string
    const location =
      event.location?.address ||
      event.location?.city ||
      event.location ||
      "Location TBD";

    // Get event type
    const eventType = event.type || "Event";

    // Calculate registrations (adjust based on your API structure)
    const currentRegistrations =
      event.registrations?.current || event.tickets_sold || 0;
    const capacity =
      event.number_of_seats || event.registrations?.capacity || 0;

    return {
      id:
        event.id?.toString() ||
        event.event_id?.toString() ||
        Math.random().toString(),
      name: event.name || event.title || "Untitled Event",
      schedule: formatEventSchedule(eventDate),
      status,
      registrations: {
        current: currentRegistrations,
        capacity: capacity,
      },
      cover:
        event.cover_image ||
        event.image ||
        "https://images.unsplash.com/photo-1540575467063-178a50c2df87?auto=format&fit=crop&w=1600&q=80",
      daysToGo,
      location:
        typeof location === "string"
          ? location
          : `${location.address || ""} ${location.city || ""}`.trim(),
      type: eventType,
      watchers: event.watchers || event.watchlist_count || 0,
      conversion:
        capacity > 0 ? Math.round((currentRegistrations / capacity) * 100) : 0,
      revenue: event.revenue || "$0",
      highlights: event.highlights || event.tags || [],
    };
  };

  // Transform user events
  const userEvents = useMemo(() => {
    return userEventsRaw.map(mapEventToEventItem);
  }, [userEventsRaw]);

  // Transform organization events
  const orgEventsMapped = useMemo(() => {
    return organizationEvents.map(mapEventToEventItem);
  }, [organizationEvents]);

  // Show events based on filter
  const allEvents = useMemo(() => {
    if (eventTypeFilter === "user") {
      return userEvents;
    } else {
      return orgEventsMapped;
    }
  }, [eventTypeFilter, userEvents, orgEventsMapped]);

  useEffect(() => {
    const handleFilterClick = (evt: MouseEvent) => {
      if (!filterRef.current) return;
      if (filterRef.current.contains(evt.target as Node)) return;
      setFilterOpen(false);
    };

    document.addEventListener("mousedown", handleFilterClick);
    return () => document.removeEventListener("mousedown", handleFilterClick);
  }, []);

  const filteredEvents = useMemo(() => {
    return allEvents.filter((event: EventItem) => {
      const matchesFilter =
        statusFilter === "all"
          ? true
          : event.status.toLowerCase() === statusFilter;

      return matchesFilter;
    });
  }, [statusFilter, allEvents]);

  const currentFilterLabel =
    statusFilters.find((filter) => filter.value === statusFilter)?.label ??
    "All upcoming";

  return (
    <section className="events-page">
      <div className="events-page__intro">
        <div className="events-page__copy">
          <p className="eyebrow">My events</p>
          <h1>Plan, publish & scale immersive experiences</h1>
          <p>
            Keep a tight pulse on every activation with a workspace that brings
            creation, collaboration, and attendee insights together.
          </p>
        </div>
        <Link to="/d/events/new">
          {" "}
          <button className="cta-button">
            <span>＋</span>
            New event
          </button>{" "}
        </Link>
      </div>

      <div className="events-toolbar">
        {/* Event Type Filter */}
        <div
          style={{
            display: "flex",
            gap: "12px",
            alignItems: "center",
            marginBottom: "16px",
            paddingBottom: "16px",
            borderBottom: "1px solid #e4e7ec",
          }}
        >
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <span
              style={{
                fontSize: "0.875rem",
                fontWeight: 600,
                color: "#344054",
              }}
            >
              Event Type:
            </span>
            <div
              style={{
                display: "flex",
                gap: "4px",
                background: "#f2f4f7",
                padding: "4px",
                borderRadius: "8px",
              }}
            >
              <button
                type="button"
                onClick={() => setEventTypeFilter("user")}
                style={{
                  padding: "6px 12px",
                  borderRadius: "6px",
                  border: "none",
                  background:
                    eventTypeFilter === "user" ? "#ffffff" : "transparent",
                  color: eventTypeFilter === "user" ? "#0f5eea" : "#667085",
                  fontWeight: eventTypeFilter === "user" ? 600 : 400,
                  cursor: "pointer",
                  fontSize: "0.875rem",
                  boxShadow:
                    eventTypeFilter === "user"
                      ? "0 1px 2px rgba(0,0,0,0.05)"
                      : "none",
                  transition: "all 0.2s ease",
                }}
              >
                User Events
              </button>
              <button
                type="button"
                onClick={() => setEventTypeFilter("organization")}
                style={{
                  padding: "6px 12px",
                  borderRadius: "6px",
                  border: "none",
                  background:
                    eventTypeFilter === "organization"
                      ? "#ffffff"
                      : "transparent",
                  color:
                    eventTypeFilter === "organization" ? "#0f5eea" : "#667085",
                  fontWeight: eventTypeFilter === "organization" ? 600 : 400,
                  cursor: "pointer",
                  fontSize: "0.875rem",
                  boxShadow:
                    eventTypeFilter === "organization"
                      ? "0 1px 2px rgba(0,0,0,0.05)"
                      : "none",
                  transition: "all 0.2s ease",
                }}
              >
                Organization Events
              </button>
            </div>
          </div>

          {eventTypeFilter === "organization" && (
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <span
                style={{
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  color: "#344054",
                }}
              >
                Organization:
              </span>
              <Select
                placeholder="Select organization"
                data={organizationOptions}
                value={selectedOrganizationId?.toString() || null}
                onChange={(value) =>
                  setSelectedOrganizationId(value ? parseInt(value, 10) : null)
                }
                searchable
                disabled={isLoadingOrgs || organizationOptions.length === 0}
                style={{ minWidth: "200px" }}
                styles={{
                  input: {
                    border: "1.5px solid #d0d5dd",
                    borderRadius: "6px",
                    fontSize: "0.875rem",
                  },
                }}
              />
            </div>
          )}
        </div>

        <div className="events-toolbar__filters">
          <div className="filter-control" ref={filterRef}>
            <button
              type="button"
              className="filter-pill"
              aria-haspopup="true"
              aria-expanded={filterOpen}
              onClick={() => setFilterOpen((prev) => !prev)}
            >
              {currentFilterLabel}
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  d="m7 10 5 5 5-5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            <div
              className={`filter-dropdown ${filterOpen ? "is-visible" : ""}`}
              role="menu"
            >
              {statusFilters.map((filter) => (
                <button
                  key={filter.value}
                  type="button"
                  role="menuitem"
                  className={
                    statusFilter === filter.value ? "is-active" : undefined
                  }
                  onClick={() => {
                    setStatusFilter(filter.value);
                    setFilterOpen(false);
                  }}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>

          <div className="view-toggle">
            <button
              type="button"
              className={viewMode === "grid" ? "is-active" : undefined}
              aria-label="Grid view"
              aria-pressed={viewMode === "grid"}
              onClick={() => setViewMode("grid")}
            >
              <span />
            </button>
            <button
              type="button"
              className={viewMode === "list" ? "is-active" : undefined}
              aria-label="List view"
              aria-pressed={viewMode === "list"}
              onClick={() => setViewMode("list")}
            >
              <span />
            </button>
          </div>
        </div>
      </div>

      {isLoadingUserEvents ||
      isLoadingOrgEvents ||
      (eventTypeFilter === "organization" && isLoadingOrgs) ? (
        <div
          className="events-empty"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "300px",
          }}
        >
          <Loader size="lg" variant="primary" />
        </div>
      ) : filteredEvents.length === 0 ? (
        <EmptyState
          icon={
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M8 2v4" />
              <path d="M16 2v4" />
              <rect width="18" height="18" x="3" y="4" rx="2" />
              <path d="M3 10h18" />
              <path d="M8 14h.01" />
              <path d="M12 14h.01" />
              <path d="M16 14h.01" />
              <path d="M8 18h.01" />
              <path d="M12 18h.01" />
              <path d="M16 18h.01" />
            </svg>
          }
          title={
            eventTypeFilter === "organization" && !selectedOrganizationId
              ? "Select an organization"
              : "No events found"
          }
          description={
            eventTypeFilter === "organization" && !selectedOrganizationId ? (
              <p>Please select an organization to view events.</p>
            ) : (
              <>
                <p>No events matched those filters.</p>
                <p>
                  Try searching by a different name or switch the status filter.
                </p>
              </>
            )
          }
          fullWidth
        />
      ) : (
        <div className={`events-grid ${viewMode === "list" ? "is-list" : ""}`}>
          {filteredEvents.map((event: EventItem) => (
            <EventCard key={event.id} event={event} layout={viewMode} />
          ))}
        </div>
      )}
    </section>
  );
};

const statusPalettes = {
  Draft: {
    accent: "#f2b01d",
    secondary: "rgba(242, 176, 29, 0.18)",
    surface: "rgba(242, 176, 29, 0.12)",
  },
  Published: {
    accent: "#0f5eea",
    secondary: "rgba(15, 94, 234, 0.22)",
    surface: "rgba(15, 94, 234, 0.12)",
  },
  Live: {
    accent: "#12b76a",
    secondary: "rgba(18, 183, 106, 0.2)",
    surface: "rgba(18, 183, 106, 0.12)",
  },
} as const;

const EventCard: React.FC<{ event: EventItem; layout: "grid" | "list" }> = ({
  event,
  layout,
}) => {
  const navigate = useNavigate();
  const palette = statusPalettes[event.status];

  const accentStyle = {
    "--accent-color": palette.accent,
    "--accent-secondary": palette.secondary,
    "--accent-surface": palette.surface,
  } as React.CSSProperties;

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClick = (evt: MouseEvent) => {
      if (!menuRef.current) return;
      if (menuRef.current.contains(evt.target as Node)) return;
      setMenuOpen(false);
    };

    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const progress =
    (event.registrations.current / event.registrations.capacity) * 100;

  return (
    <article className={`event-card layout-${layout}`} style={accentStyle}>
      <Link
        to={`/d/events/${event.id}`}
        className="event-card__link"
        onClick={(e) => {
          // Don't navigate if clicking on buttons or menu
          const target = e.target as HTMLElement;
          if (
            target.closest("button") ||
            target.closest(".event-card__menu") ||
            target.closest(".ghost-icon")
          ) {
            e.preventDefault();
          }
        }}
      >
        <div className="event-card__cover">
          <img src={event.cover} alt={event.name} />
          <div className="event-card__cover-overlay" />
          <div className="event-card__cover-top">
            <span
              className={`event-card__badge status-${event.status.toLowerCase()}`}
            >
              {event.status}
            </span>
            <button className="ghost-icon is-light" aria-label="Share event">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  d="M18 8a3 3 0 1 0-2.83-4 3 3 0 0 0 2.83 4m-12 10a3 3 0 1 0 2.82 4A3 3 0 0 0 6 18m0-10a3 3 0 1 0-2.82-4A3 3 0 0 0 6 8m11.17 1.25-8.34 4.5m0-5.5 8.34 4.5"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
          <div className="event-card__cover-bottom">
            <div>
              <p className="timer-label">Days to go</p>
              <p className="timer-value">
                {event.daysToGo} {event.daysToGo === 1 ? "day" : "days"}
              </p>
            </div>
            <div>
              <p className="timer-label">Watchlist</p>
              <p className="timer-value">{event.watchers}</p>
            </div>
          </div>
        </div>

        <div className="event-card__body">
          <div className="event-card__heading">
            <div>
              <p className="event-type">{event.type}</p>
              <h3>{event.name}</h3>
              <p className="schedule">{event.schedule}</p>
            </div>
            <button className="event-card__quick-action">Manage</button>
          </div>

          <div className="event-card__chips">
            <span>{event.location}</span>
            <span>
              {Math.max(
                event.registrations.capacity - event.registrations.current,
                0
              )}{" "}
              seats left
            </span>
          </div>

          <div className="event-card__meta">
            <div>
              <span className="meta-label">Registrations</span>
              <span className="meta-value">
                {event.registrations.current}/{event.registrations.capacity}
              </span>
            </div>
            <div className="event-card__menu-wrap" ref={menuRef}>
              <button
                className="ghost-icon"
                aria-label="Event actions"
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen((prev) => !prev)}
              >
                <svg
                  width="4"
                  height="16"
                  viewBox="0 0 4 16"
                  aria-hidden="true"
                >
                  <circle cx="2" cy="2" r="2" />
                  <circle cx="2" cy="8" r="2" />
                  <circle cx="2" cy="14" r="2" />
                </svg>
              </button>

              <div
                className={`event-card__menu ${menuOpen ? "is-visible" : ""}`}
                role="menu"
              >
                <button
                  type="button"
                  role="menuitem"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setMenuOpen(false);
                    navigate(`/d/events/${event.id}/edit`);
                  }}
                >
                  Edit
                </button>
                <button type="button" role="menuitem">
                  View event
                </button>
                <button type="button" role="menuitem">
                  Clone
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="is-destructive"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>

          <div className="event-card__progress">
            <div className="progress-bar">
              <span style={{ width: `${progress}%` }} />
            </div>
            <span className="progress-label">
              {progress.toFixed(0)}% of capacity
            </span>
          </div>

          <div className="event-card__stats-panel">
            <div className="event-card__metrics">
              <div>
                <p className="metric-label">Conversion</p>
                <p className="metric-value">{event.conversion}%</p>
              </div>
              <div>
                <p className="metric-label">Revenue</p>
                <p className="metric-value">{event.revenue}</p>
              </div>
              <div>
                <p className="metric-label">Highlights</p>
                <p className="metric-value">{event.highlights.length}</p>
              </div>
            </div>
            <button className="event-card__cta">Share RSVP</button>
          </div>

          <div className="event-card__tags">
            {event.highlights.map((tag) => (
              <span key={tag}>{tag}</span>
            ))}
          </div>
        </div>
      </Link>
    </article>
  );
};

export default EventsPage;
